import { useEffect, useState } from "react";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { Palette, Save, Upload } from "lucide-react";

export default function Branding() {
  const { brand, save } = useBranding();
  const [form, setForm] = useState(brand);
  const [busy, setBusy] = useState(false);

  useEffect(() => setForm(brand), [brand]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo must be smaller than 500 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await save(form);
      toast.success("Branding updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="branding-page">
      <div>
        <div className="text-xs uppercase tracking-widest" style={{ color: "var(--accent)" }}>
          Company
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Branding</h1>
        <p className="text-slate-400 mt-2 text-sm">Personalize the platform with your company name, logo, and accent color.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="card-surface p-6 space-y-4 lg:col-span-2">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Company name</label>
            <input
              value={form.company_name || ""}
              onChange={(e) => set("company_name", e.target.value)}
              data-testid="brand-name"
              required
              className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-4 py-2.5 outline-none focus:border-[color:var(--accent)]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Tagline</label>
            <input
              value={form.tagline || ""}
              onChange={(e) => set("tagline", e.target.value)}
              data-testid="brand-tagline"
              className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-4 py-2.5 outline-none focus:border-[color:var(--accent)]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Logo</label>
            <div className="mt-2 flex items-center gap-3">
              <input
                value={form.logo_url || ""}
                onChange={(e) => set("logo_url", e.target.value)}
                placeholder="Paste an image URL, or upload below"
                data-testid="brand-logo-url"
                className="flex-1 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-4 py-2.5 outline-none focus:border-[color:var(--accent)]"
              />
              <label className="cursor-pointer flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#2a3447] hover:border-[color:var(--accent)] text-slate-300 hover:text-white transition-colors">
                <Upload size={14} /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="brand-logo-upload" />
              </label>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">SVG or PNG recommended. Max 500 KB.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Accent color</label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  value={form.accent_color || "var(--accent)"}
                  onChange={(e) => set("accent_color", e.target.value)}
                  data-testid="brand-accent"
                  className="w-12 h-11 rounded-lg bg-transparent border border-[#2a3447] cursor-pointer"
                />
                <input
                  value={form.accent_color || ""}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="flex-1 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2.5 outline-none focus:border-[color:var(--accent)] font-mono text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Accent hover</label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  value={form.accent_hover || "#d10000"}
                  onChange={(e) => set("accent_hover", e.target.value)}
                  data-testid="brand-accent-hover"
                  className="w-12 h-11 rounded-lg bg-transparent border border-[#2a3447] cursor-pointer"
                />
                <input
                  value={form.accent_hover || ""}
                  onChange={(e) => set("accent_hover", e.target.value)}
                  className="flex-1 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2.5 outline-none focus:border-[color:var(--accent)] font-mono text-sm"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Login hero image URL</label>
            <input
              value={form.login_hero_url || ""}
              onChange={(e) => set("login_hero_url", e.target.value)}
              data-testid="brand-hero"
              className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-4 py-2.5 outline-none focus:border-[color:var(--accent)]"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="brand-save"
              className="px-5 py-2.5 rounded-lg font-bold text-black flex items-center gap-2 disabled:opacity-60"
              style={{ background: "var(--accent)" }}
            >
              <Save size={16} /> {busy ? "Saving…" : "Save branding"}
            </button>
          </div>
        </form>

        {/* Live preview */}
        <div className="space-y-4">
          <div className="card-surface p-6" data-testid="brand-preview">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Live preview</div>
            <div className="flex items-center gap-3 mb-4">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="w-11 h-11 rounded-xl object-cover bg-white" />
              ) : (
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-black font-bold"
                  style={{ background: form.accent_color || "var(--accent)" }}
                >
                  <Palette size={22} strokeWidth={2.5} />
                </div>
              )}
              <div>
                <div className="font-bold text-lg leading-none">{form.company_name || "Company"}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{form.tagline}</div>
              </div>
            </div>
            <button
              type="button"
              className="w-full py-2.5 rounded-lg text-black font-bold transition-colors"
              style={{ background: form.accent_color || "var(--accent)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = form.accent_hover || "#d10000")}
              onMouseLeave={(e) => (e.currentTarget.style.background = form.accent_color || "var(--accent)")}
            >
              Primary Action
            </button>
            <div
              className="mt-3 text-xs px-3 py-2 rounded-lg border"
              style={{
                background: `${form.accent_color}15`,
                borderColor: `${form.accent_color}40`,
                color: form.accent_color,
              }}
            >
              Accent surface uses your color
            </div>
          </div>

          <div className="card-surface p-6">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Custom domain</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              After you deploy, you can point a custom domain (e.g. <span className="font-mono text-white">hr.yourcompany.com</span>) at
              the app from the deployment settings. All branding here will follow automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
